import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './api';
import { CreatePromoterRequest, ImportedContact, TeamMemberSummary } from '@clareefai/types';
export type { TeamMemberSummary };

export interface PromoterProgressResponse {
  reviewCompleted: boolean;
  testimonialCompleted: boolean;
  useCaseCompleted: boolean;
  firstMeetingCompleted: boolean;
}

export interface PromoterVIPStatusResponse {
  isVIP: boolean;
  requestedAt?: string;
  approvedAt?: string;
  benefits: string[];
}

export interface PromoterTestimonialResponse {
  id: string;
  type: 'REVIEW' | 'VIDEO' | 'USE_CASE';
  status: 'COMPLETED' | 'PUBLISHED' | 'FEATURED' | 'APPROVED'; // APPROVED is legacy, maps to PUBLISHED
  productId?: string;
  rating?: number;
  content?: string;
  videoUrl?: string;
  videoTitle?: string;
  videoDescription?: string;
  videoTags?: string[];
  videoDuration?: number;
  vendorResponse?: string;
  rejectionReason?: string;
  isFeatured: boolean;
  createdAt: string;
  updatedAt: string;
  reviewedAt?: string;
}

export interface PromoterMeetingDateResponse {
  id: string;
  date: string;
  time: string;
  status: string;
}

export interface PromoterMeetingResponse {
  id: string;
  prospect_id: string;
  prospect_name?: string | null;
  prospect_company?: string | null;
  prospect_email?: string | null;
  prospect_avatar?: string | null;
  promoter_name?: string | null;
  type: string;
  status: string;
  requested_dates: PromoterMeetingDateResponse[];
  rescheduled_dates: PromoterMeetingDateResponse[];
  accepted_date?: PromoterMeetingDateResponse | null;
  timezone?: string | null;
  notes?: string | null;
  join_url?: string | null;
  outcome?: string | null;
  rating?: number | null;
  date?: string | null;
  time?: string | null;
  prospect_joined_at?: string | null;
  promoter_joined_at?: string | null;
  meeting_started_at?: string | null;
  meeting_ended_at?: string | null;
  duration_minutes?: number | null;
  created_date: string;
  created_at: string;
}

export interface PromoterResponse {
  id: string;
  vendorId: string;
  firebaseUid: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  company?: string;
  linkedin?: string;
  companySize?: string;
  industrySectors?: string[];
  productNames?: string[];
  avatar?: string;
  companyLogo?: string;
  status?: string;
  prospectsReferred: number;
  conversionRate: number;
  totalRewards: number;
  assignedProspectsCount?: number;
  joinedDate?: string;
  vipStatus?: PromoterVIPStatusResponse;
  progress?: PromoterProgressResponse;
  metadata?: Record<string, string>;
  tags?: string[];
  customFields?: Record<string, string>;
  importSource?: string | null;
  testimonials?: PromoterTestimonialResponse[];
  meetings?: PromoterMeetingResponse[];
  referenceCallFrequency?: string;
  timezone?: string;
  reviewReminderSentAt?: string | null;
  useCaseReminderSentAt?: string | null;
  firstMeetingReminderSentAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
  isEmailVerified?: boolean;
  createdByTeamMember?: TeamMemberSummary;
  assignedTeamMember?: TeamMemberSummary;
  createdByType?: string;
  approvalStatus?: 'PENDING_APPROVAL' | 'APPROVED' | 'REJECTED' | null;
}

export interface PromoterStatsResponse {
  total: number;
  invited: number;
  readyForCall: number;
  totalRewards: number;
}

export type ActionReminderType = 'REVIEW' | 'USE_CASE' | 'FIRST_MEETING';

// Batch Import types
export interface BatchImportPromoterEntry {
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  company?: string;
  linkedin?: string;
  company_size?: string;
  industry_sectors?: string[];
}

export interface BatchImportRequest {
  promoters: BatchImportPromoterEntry[];
  import_source: 'csv' | 'hubspot' | 'salesforce';
  import_metadata?: Record<string, string>;
}

export interface BatchImportResult {
  email: string;
  status: 'success' | 'duplicate' | 'error';
  error?: string;
}

export interface BatchImportResponse {
  success_count: number;
  duplicate_count: number;
  error_count: number;
  results: BatchImportResult[];
}

// HubSpot types
export interface HubSpotAuthUrlResponse {
  auth_url: string;
}

export interface HubSpotConnectionStatus {
  connected: boolean;
  hub_id?: string;
}

export interface HubSpotContact {
  id: string;
  properties: Record<string, string | null>;
}

export interface HubSpotContactsResponse {
  contacts: HubSpotContact[];
  has_more: boolean;
  after?: string;
  total: number;
}

export interface HubSpotProperty {
  name: string;
  label: string;
  type: string;
  description?: string;
}

export interface HubSpotPropertiesResponse {
  properties: HubSpotProperty[];
}

// Salesforce types
export interface SalesforceAuthUrlResponse {
  auth_url: string;
}

export interface SalesforceConnectionStatus {
  connected: boolean;
  org_id?: string;
  instance_url?: string;
}

export type SalesforceObjectType = 'contact' | 'lead';

export interface SalesforceContact {
  id: string;
  properties: Record<string, string | null>;
}

export interface SalesforceContactsResponse {
  contacts: SalesforceContact[];
  has_more: boolean;
  after?: string;
  total: number;
}

export const promoterApi = createApi({
  reducerPath: 'promoterApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Promoter', 'HubSpot', 'Salesforce', 'ImportedContact'],
  endpoints: (builder) => ({
    getPromoters: builder.query<PromoterResponse[], void>({
      query: () => '/promoters',
      providesTags: ['Promoter'],
    }),
    getVerifiedPromoters: builder.query<PromoterResponse[], void>({
      query: () => '/promoters/verified',
      providesTags: ['Promoter'],
    }),
    getPromoterStats: builder.query<PromoterStatsResponse, void>({
      query: () => '/promoters/stats',
      providesTags: ['Promoter'],
    }),
    getPromoterById: builder.query<PromoterResponse, string>({
      query: (id) => `/promoters/${id}`,
      providesTags: (result, error, id) => [{ type: 'Promoter', id }],
    }),
    createPromoter: builder.mutation<PromoterResponse, CreatePromoterRequest>({
      query: (body) => ({
        url: '/promoters',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Promoter'],
    }),
    sendActionReminder: builder.mutation<void, { promoterId: string; action: ActionReminderType }>({
      query: ({ promoterId, action }) => ({
        url: `/promoters/${promoterId}/send-action-reminder?action=${action}`,
        method: 'POST',
      }),
      invalidatesTags: (_, __, { promoterId }) => [{ type: 'Promoter', id: promoterId }],
    }),
    updatePromoter: builder.mutation<PromoterResponse, { id: string; data: Record<string, any> }>({
      query: ({ id, data }) => ({
        url: `/promoters/${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Promoter', id }, 'Promoter'],
    }),
    deletePromoter: builder.mutation<void, string>({
      query: (id) => ({
        url: `/promoters/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Promoter'],
    }),
    uploadPromoterCompanyLogo: builder.mutation<PromoterResponse, { id: string; file: File }>({
      query: ({ id, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: `/promoters/${id}/company-logo`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Promoter', id }, 'Promoter'],
    }),
    resendPromoterInvitation: builder.mutation<{ message: string }, string>({
      query: (promoterId) => ({
        url: `/promoters/${promoterId}/resend-invitation`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, promoterId) => [{ type: 'Promoter', id: promoterId }],
    }),

    // Imported Contacts (staging)
    getImportedContacts: builder.query<ImportedContact[], void>({
      query: () => '/promoters/imported',
      providesTags: ['ImportedContact'],
    }),
    inviteImportedContact: builder.mutation<PromoterResponse, string>({
      query: (id) => ({ url: `/promoters/imported/${id}/invite`, method: 'POST' }),
      invalidatesTags: ['ImportedContact', 'Promoter'],
    }),
    inviteImportedContactsBatch: builder.mutation<BatchImportResponse, { ids: string[] }>({
      query: (body) => ({ url: '/promoters/imported/invite-batch', method: 'POST', body }),
      invalidatesTags: ['ImportedContact', 'Promoter'],
    }),
    deleteImportedContact: builder.mutation<void, string>({
      query: (id) => ({ url: `/promoters/imported/${id}`, method: 'DELETE' }),
      invalidatesTags: ['ImportedContact'],
    }),
    assignImportedContacts: builder.mutation<ImportedContact[], { contactIds: string[]; teamMemberId: string }>({
      query: (body) => ({ url: '/promoters/imported/assign', method: 'POST', body }),
      invalidatesTags: ['ImportedContact'],
    }),

    // Batch Import
    batchImportPromoters: builder.mutation<BatchImportResponse, BatchImportRequest>({
      query: (body) => ({
        url: '/promoters/batch-import',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Promoter'],
    }),

    // HubSpot Integration
    getHubSpotAuthUrl: builder.query<HubSpotAuthUrlResponse, void>({
      query: () => '/vendors/me/integrations/hubspot/auth-url',
    }),
    hubSpotCallback: builder.mutation<HubSpotConnectionStatus, { code: string }>({
      query: (body) => ({
        url: '/vendors/me/integrations/hubspot/callback',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['HubSpot'],
    }),
    getHubSpotStatus: builder.query<HubSpotConnectionStatus, void>({
      query: () => '/vendors/me/integrations/hubspot/status',
      providesTags: ['HubSpot'],
    }),
    getHubSpotContacts: builder.query<HubSpotContactsResponse, { limit?: number; after?: string }>({
      query: ({ limit = 100, after }) => {
        let url = `/vendors/me/integrations/hubspot/contacts?limit=${limit}`;
        if (after) url += `&after=${after}`;
        return url;
      },
    }),
    searchHubSpotContacts: builder.query<HubSpotContactsResponse, { q: string; limit?: number }>({
      query: ({ q, limit = 10 }) =>
        `/vendors/me/integrations/hubspot/contacts/search?q=${encodeURIComponent(q)}&limit=${limit}`,
    }),
    getHubSpotContactProperties: builder.query<HubSpotPropertiesResponse, void>({
      query: () => '/vendors/me/integrations/hubspot/contact-properties',
    }),
    disconnectHubSpot: builder.mutation<void, void>({
      query: () => ({
        url: '/vendors/me/integrations/hubspot/disconnect',
        method: 'DELETE',
      }),
      invalidatesTags: ['HubSpot'],
    }),

    // Salesforce Integration
    getSalesforceAuthUrl: builder.query<SalesforceAuthUrlResponse, void>({
      query: () => '/vendors/me/integrations/salesforce/auth-url',
    }),
    salesforceCallback: builder.mutation<SalesforceConnectionStatus, { code: string; state: string }>({
      query: (body) => ({
        url: '/vendors/me/integrations/salesforce/callback',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Salesforce'],
    }),
    getSalesforceStatus: builder.query<SalesforceConnectionStatus, void>({
      query: () => '/vendors/me/integrations/salesforce/status',
      providesTags: ['Salesforce'],
    }),
    getSalesforceContacts: builder.query<SalesforceContactsResponse, { limit?: number; after?: string; objectType?: SalesforceObjectType }>({
      query: ({ limit = 100, after, objectType = 'contact' }) => {
        let url = `/vendors/me/integrations/salesforce/contacts?limit=${limit}&objectType=${objectType}`;
        if (after) url += `&after=${after}`;
        return url;
      },
    }),
    searchSalesforceContacts: builder.query<SalesforceContactsResponse, { q: string; limit?: number; objectType?: SalesforceObjectType }>({
      query: ({ q, limit = 10, objectType = 'contact' }) =>
        `/vendors/me/integrations/salesforce/contacts/search?q=${encodeURIComponent(q)}&limit=${limit}&objectType=${objectType}`,
    }),
    disconnectSalesforce: builder.mutation<void, void>({
      query: () => ({
        url: '/vendors/me/integrations/salesforce/disconnect',
        method: 'DELETE',
      }),
      invalidatesTags: ['Salesforce'],
    }),

    approveWidgetPromoter: builder.mutation<PromoterResponse, string>({
      query: (promoterId) => ({
        url: `/promoters/${promoterId}/approve-widget`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, promoterId) => [{ type: 'Promoter', id: promoterId }, 'Promoter'],
    }),
    rejectWidgetPromoter: builder.mutation<PromoterResponse, string>({
      query: (promoterId) => ({
        url: `/promoters/${promoterId}/reject-widget`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, promoterId) => [{ type: 'Promoter', id: promoterId }, 'Promoter'],
    }),

    assignPromoterToTeamMember: builder.mutation<PromoterResponse, { promoterId: string; teamMemberId: string | null }>({
      query: ({ promoterId, teamMemberId }) => ({
        url: `/promoters/${promoterId}/assign-team-member`,
        method: 'POST',
        body: { teamMemberId },
      }),
      invalidatesTags: (_result, _error, { promoterId }) => [
        { type: 'Promoter', id: promoterId },
        { type: 'Promoter', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetPromotersQuery,
  useGetVerifiedPromotersQuery,
  useGetPromoterStatsQuery,
  useGetPromoterByIdQuery,
  useCreatePromoterMutation,
  useSendActionReminderMutation,
  useUpdatePromoterMutation,
  useDeletePromoterMutation,
  useUploadPromoterCompanyLogoMutation,
  useResendPromoterInvitationMutation,
  useBatchImportPromotersMutation,
  useGetImportedContactsQuery,
  useInviteImportedContactMutation,
  useInviteImportedContactsBatchMutation,
  useDeleteImportedContactMutation,
  useAssignImportedContactsMutation,
  useGetHubSpotAuthUrlQuery,
  useLazyGetHubSpotAuthUrlQuery,
  useHubSpotCallbackMutation,
  useGetHubSpotStatusQuery,
  useGetHubSpotContactsQuery,
  useLazyGetHubSpotContactsQuery,
  useGetHubSpotContactPropertiesQuery,
  useDisconnectHubSpotMutation,
  useLazySearchHubSpotContactsQuery,
  useGetSalesforceAuthUrlQuery,
  useSalesforceCallbackMutation,
  useGetSalesforceStatusQuery,
  useLazyGetSalesforceContactsQuery,
  useLazySearchSalesforceContactsQuery,
  useDisconnectSalesforceMutation,
  useAssignPromoterToTeamMemberMutation,
  useApproveWidgetPromoterMutation,
  useRejectWidgetPromoterMutation,
} = promoterApi;
